import * as React from "react";
import {
  createFileRoute,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { auth } from "../../config/firebase";
import {
  getAdditionalUserInfo,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { reportError } from "../../utils/errorReporting";
import {
  classifyMagicLinkCallbackError,
  getAuthErrorCode,
  type MagicLinkCallbackFailure,
} from "../../utils/authErrors";
import {
  trackAuthCompleted,
  trackAuthFailed,
} from "../../services/analyticsService";
import {
  clearPendingAuthFlowContext,
  readPendingAuthFlowContext,
} from "../../services/authFlowContext";

// The failures that end in a terminal screen. The two recoverable ones
// (email mismatch, retryable) re-show the email form instead.
type CallbackErrorKind = Exclude<
  MagicLinkCallbackFailure,
  "email_mismatch" | "retryable"
>;

const AuthCallbackLayout: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <main className="min-h-screen w-full overflow-y-auto bg-white">
    <div className="mx-auto flex min-h-screen w-full max-w-lg items-center px-8 py-10 sm:px-10">
      <div className="w-full text-center">
        <img
          alt="ShopVibes"
          className="mx-auto h-9 w-auto"
          src="/studio_logo.svg"
          style={{ width: 124 }}
        />
        {children}
      </div>
    </div>
  </main>
);

const AuthCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signInLink } = useRouteContext({ from: "/auth/callback" });
  const [pendingContext] = React.useState(readPendingAuthFlowContext);
  const [errorKind, setErrorKind] = React.useState<CallbackErrorKind | null>(
    null
  );
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [needsEmail, setNeedsEmail] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const hasAttempted = React.useRef(false);

  const trackCallbackFailure = React.useCallback(
    (reason: string) => {
      trackAuthFailed({
        mode: pendingContext?.mode ?? "unknown",
        method: "magic_link",
        source: pendingContext?.source ?? "unknown",
        hasPrefilledEmail: pendingContext?.hasPrefilledEmail ?? false,
        reason,
        stage: "callback",
      });
    },
    [pendingContext]
  );

  const completeSignIn = React.useCallback(
    async (signInEmail: string, emailReentered: boolean) => {
      setLoading(true);
      setEmailError(null);
      setErrorKind(null);

      try {
        const userCredential = await signInWithEmailLink(
          auth,
          signInEmail,
          signInLink
        );
        const isNewUser =
          getAdditionalUserInfo(userCredential)?.isNewUser === true;

        trackAuthCompleted(userCredential.user.uid, {
          mode: isNewUser ? "signup" : "login",
          requestedMode: pendingContext?.mode ?? "unknown",
          method: "magic_link",
          source: pendingContext?.source ?? "unknown",
          hasPrefilledEmail: pendingContext?.hasPrefilledEmail ?? false,
          emailReentered,
        });
        window.localStorage.removeItem("emailForSignIn");
        clearPendingAuthFlowContext();
        // Navigation handled by __root.tsx via onAuthStateChanged.
      } catch (error: unknown) {
        const failure = classifyMagicLinkCallbackError(error);
        trackCallbackFailure(
          failure === "disposable"
            ? "disposable_email"
            : getAuthErrorCode(error)
        );
        // A new account on a throwaway domain is rejected by the
        // blockDisposableEmailSignup blocking function on its first magic-link
        // sign-in. That is the feature working, not a fault, so it stays out
        // of Sentry (which would also carry the address via the auth error).
        if (failure !== "disposable") {
          reportError(error, { context: "completeEmailLinkSignIn" });
        }

        switch (failure) {
          case "email_mismatch":
            if (emailReentered) {
              setEmailError(t("login.callbackEmailRetry"));
            } else {
              window.localStorage.removeItem("emailForSignIn");
              setEmail("");
            }
            setNeedsEmail(true);
            return;
          case "retryable":
            setEmail(signInEmail);
            setEmailError(t("login.callbackTemporaryError"));
            setNeedsEmail(true);
            return;
          default:
            setNeedsEmail(false);
            setErrorKind(failure);
        }
      } finally {
        setLoading(false);
      }
    },
    [pendingContext, signInLink, t, trackCallbackFailure]
  );

  const handleEmailSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    completeSignIn(email, true);
  };

  const handleRequestNewLink = () => {
    const mode = pendingContext?.mode ?? "login";
    window.localStorage.removeItem("emailForSignIn");
    clearPendingAuthFlowContext();
    navigate({ to: "/login", search: { mode } });
  };

  React.useEffect(() => {
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    if (!isSignInWithEmailLink(auth, signInLink)) {
      trackCallbackFailure("invalid_email_link");
      setErrorKind("invalid");
      return;
    }

    const storedEmail = window.localStorage.getItem("emailForSignIn");
    if (!storedEmail) {
      setNeedsEmail(true);
      return;
    }

    completeSignIn(storedEmail, false);
  }, [completeSignIn, signInLink, trackCallbackFailure]);

  if (needsEmail) {
    return (
      <AuthCallbackLayout>
        <div className="mx-auto mt-10 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
          <svg
            aria-hidden="true"
            className="h-8 w-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 0h10.5A1.75 1.75 0 0119 12.25v7A1.75 1.75 0 0117.25 21H6.75A1.75 1.75 0 015 19.25v-7a1.75 1.75 0 011.75-1.75z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        </div>

        <h1 className="mt-5 text-3xl font-semibold tracking-tight text-gray-900">
          {t("login.callbackConfirmTitle")}
        </h1>
        <p className="mt-3 text-sm leading-6 text-gray-600">
          {t("login.callbackConfirmDesc")}
        </p>

        <form className="mt-8 text-left" onSubmit={handleEmailSubmit}>
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="callback-email"
          >
            {t("login.emailAddress")}
          </label>
          <input
            id="callback-email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-500 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
            placeholder={t("login.emailPlaceholder")}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <p className="mt-2 text-xs leading-5 text-gray-500">
            {t("login.callbackConfirmHelper")}
          </p>

          {emailError && (
            <div
              className="mt-5 rounded-xl border border-red-100 bg-red-50 p-3 text-sm leading-5 text-red-700"
              role="alert"
            >
              {emailError}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-6 flex w-full items-center justify-center rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {loading
              ? t("login.processing")
              : t("login.callbackCompleteSignIn")}
          </button>
        </form>

        <button
          type="button"
          onClick={handleRequestNewLink}
          className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          {t("login.callbackRequestNewLink")}
        </button>
      </AuthCallbackLayout>
    );
  }

  if (errorKind) {
    const copyKeys: Record<CallbackErrorKind, [string, string]> = {
      expired: ["login.callbackExpiredTitle", "login.callbackExpiredDesc"],
      app_check: ["login.callbackAppCheckTitle", "login.callbackAppCheckDesc"],
      invalid: ["login.callbackInvalidTitle", "login.callbackInvalidDesc"],
      disposable: [
        "login.callbackDisposableTitle",
        "login.callbackDisposableDesc",
      ],
      unknown: ["login.callbackUnknownTitle", "login.callbackUnknownDesc"],
    };
    const [titleKey, descriptionKey] = copyKeys[errorKind];

    return (
      <AuthCallbackLayout>
        <div className="mx-auto mt-10 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-600">
          <svg
            aria-hidden="true"
            className="h-8 w-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M12 8v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        </div>

        <h1 className="mt-5 text-3xl font-semibold tracking-tight text-gray-900">
          {t(titleKey)}
        </h1>
        <p className="mt-3 text-sm leading-6 text-gray-600">
          {t(descriptionKey)}
        </p>

        <button
          type="button"
          onClick={handleRequestNewLink}
          className="mt-8 flex w-full items-center justify-center rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
        >
          {t("login.callbackRequestNewLink")}
        </button>
      </AuthCallbackLayout>
    );
  }

  return (
    <AuthCallbackLayout>
      <div
        className="mx-auto mt-10 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-900"
        role="status"
      >
        <svg
          aria-hidden="true"
          className="h-7 w-7 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-20"
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            strokeWidth="3"
          />
          <path
            className="opacity-90"
            d="M21 12a9 9 0 00-9-9"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="3"
          />
        </svg>
      </div>
      <h1 className="mt-5 text-3xl font-semibold tracking-tight text-gray-900">
        {t("login.callbackSigningInTitle")}
      </h1>
      <p className="mt-3 text-sm leading-6 text-gray-600">
        {t("login.callbackSigningInDesc")}
      </p>
    </AuthCallbackLayout>
  );
};

export const Route = createFileRoute("/auth/callback")({
  // Capture the full URL before TanStack Router strips Firebase query params.
  beforeLoad: () => {
    return { signInLink: window.location.href };
  },
  component: AuthCallbackPage,
});
